import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CustomerAuthProvider } from './contexts/CustomerAuthContext';
import { BusinessAuthProvider } from './contexts/BusinessAuthContext';
import { VideoProvider } from './contexts/VideoContext';

// Public Components
import PublicNavBar from './components/public/PublicNavBar';
import HomePage from './components/public/HomePage';
import ForBusinessesPage from './components/public/ForBusinessesPage';

// Customer Components
import CustomerProtectedRoute from './components/customer/CustomerProtectedRoute';
import CustomerLogin from './components/customer/Login';
import CustomerRegister from './components/customer/Register';
import CustomerDashboard from './components/customer/Dashboard';
import CustomerSettings from './components/customer/Settings';
import LandingPage from './components/customer/LandingPage';
import VideoWizard from './components/customer/VideoWizard';
import VideoStatus from './components/customer/VideoStatus';
import PaymentSuccess from './components/customer/PaymentSuccess';
import PaymentCancel from './components/customer/PaymentCancel';

// Business Components
import BusinessProtectedRoute from './components/business/BusinessProtectedRoute';
import BusinessAuthPage from './components/business/AuthPage';
import BusinessDaycareNavBar from './components/business/DaycareNavBar';
import BusinessDaycareVideoForm from './components/business/DaycareVideoForm';
import BusinessVideoGallery from './components/business/VideoGallery';
import BusinessAnalytics from './components/business/Analytics';
import BusinessCustomers from './components/business/Customers';
import BusinessCustomerDetail from './components/business/CustomerDetail';

import './App.css';

// Layout wrapper to conditionally show navigation
function Layout({ children }) {
  const location = useLocation();

  // Determine which nav to show based on route
  const isPublicRoute = location.pathname === '/' || location.pathname === '/for-businesses';
  const isBusinessRoute = location.pathname.startsWith('/business/') &&
    location.pathname !== '/business/login' &&
    location.pathname !== '/business/register';

  return (
    <>
      {isPublicRoute && <PublicNavBar />}
      {isBusinessRoute && <BusinessDaycareNavBar />}
      {children}
    </>
  );
}

// Main App Routes Component
function AppRoutes() {
  return (
    <Routes>
      {/* PUBLIC ROUTES */}
      <Route path="/" element={<HomePage />} />
      <Route path="/for-businesses" element={<ForBusinessesPage />} />

      {/* CLEAN URL REDIRECTS - for landing page CTAs */}
      <Route path="/register" element={<Navigate to="/app/register" replace />} />
      <Route path="/login" element={<Navigate to="/app/login" replace />} />
      <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />

      {/* CUSTOMER ROUTES */}
      <Route path="/app/login" element={<CustomerLogin />} />
      <Route path="/app/register" element={<CustomerRegister />} />

      <Route path="/app/dashboard" element={
        <CustomerProtectedRoute>
          <CustomerDashboard />
        </CustomerProtectedRoute>
      } />

      <Route path="/app/settings" element={
        <CustomerProtectedRoute>
          <CustomerSettings />
        </CustomerProtectedRoute>
      } />

      <Route path="/app/daycare/:daycareId" element={<LandingPage />} />
      <Route path="/app/create/:daycareId" element={<VideoWizard />} />
      <Route path="/app/video/:jobId" element={<VideoStatus />} />
      <Route path="/app/payment/success" element={<PaymentSuccess />} />
      <Route path="/app/payment/cancel" element={<PaymentCancel />} />

      {/* BUSINESS ROUTES */}
      <Route path="/business/login" element={<BusinessAuthPage />} />
      <Route path="/business/register" element={<BusinessAuthPage />} />

      <Route path="/business/dashboard" element={
        <BusinessProtectedRoute>
          <div className="app-container">
            <BusinessDaycareVideoForm />
            <BusinessVideoGallery />
          </div>
        </BusinessProtectedRoute>
      } />

      <Route path="/business/videos" element={
        <BusinessProtectedRoute>
          <div className="app-container">
            <BusinessDaycareVideoForm />
            <BusinessVideoGallery />
          </div>
        </BusinessProtectedRoute>
      } />

      <Route path="/business/analytics" element={
        <BusinessProtectedRoute>
          <BusinessAnalytics />
        </BusinessProtectedRoute>
      } />

      <Route path="/business/customers" element={
        <BusinessProtectedRoute>
          <BusinessCustomers />
        </BusinessProtectedRoute>
      } />

      <Route path="/business/customers/:customerId" element={
        <BusinessProtectedRoute>
          <BusinessCustomerDetail />
        </BusinessProtectedRoute>
      } />

      {/* Default redirects */}
      <Route path="/business" element={<Navigate to="/business/dashboard" replace />} />
      <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />
    </Routes>
  );
}

// Main App Component
function App() {
  return (
    <CustomerAuthProvider>
      <BusinessAuthProvider>
        <VideoProvider>
          <Router>
            <Layout>
              <AppRoutes />
            </Layout>
          </Router>
        </VideoProvider>
      </BusinessAuthProvider>
    </CustomerAuthProvider>
  );
}

export default App;
